import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, IconButton, Tooltip, Typography, CircularProgress } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Close as CloseIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
} from "@material-ui/icons";
import { useTranslation } from "../../hooks/useTranslation";
import MediaPlayer from "../MediaPlayer";
import SpeakersBar from "./SpeakersBar";
import TranscriptionList from "./TranscriptionList";
import TranscriptionStatusBar from "./TranscriptionStatusBar";
import { hasUnsavedChanges } from "../../utils/speakerHelpers";

/**
 * Localiza qual sentença contém o instante `time` (em segundos). Implementação
 * linear; suficiente até alguns milhares de sentenças. Se nenhuma contiver o
 * instante, devolve a última sentença anterior. Devolve -1 se a lista estiver
 * vazia ou o tempo for anterior à primeira sentença.
 */
function findActiveSentenceIndex(sentences, time) {
  if (!Array.isArray(sentences) || sentences.length === 0) return -1;
  let lastBefore = -1;
  for (let i = 0; i < sentences.length; i += 1) {
    const s = sentences[i];
    const start = Number.isFinite(s?.start) ? s.start : 0;
    const end = Number.isFinite(s?.end) ? s.end : start;
    if (time >= start && time < end) return i;
    if (time >= start) lastBefore = i;
  }
  return lastBefore;
}

/**
 * Painel completo de revisão/visualização de uma transcrição. Composto por:
 *   1. Barra de status da transcrição (aprovação, salvar, downloads)
 *   2. Lista de speakers (com edição/criação/mesclagem)
 *   3. Player de áudio/vídeo (MediaPlayer)
 *   4. Lista de parágrafos da transcrição (com busca, destaque sincronizado
 *      ao player, edição de tempo/texto/speaker e split)
 *
 * Layout: status e speakers no topo (sempre visíveis); player aparece logo
 * antes da lista de frases para facilitar a revisão (o usuário tem o player
 * próximo do ponto que está editando) e a lista ocupa o restante do espaço
 * vertical. A prop `forceLayout` é mantida por compatibilidade; ainda não
 * tem efeito visual.
 *
 * O componente é controlado quanto à transcrição, mas mantém um estado
 * interno espelhado para detectar `hasUnsavedChanges` em relação ao
 * `originalTranscription` (versão recebida do backend / último save/approve).
 *
 * MODO EFETIVO E PROPAGAÇÃO DE PERMISSÕES
 * ---------------------------------------
 * O painel é a ÚNICA fonte da verdade sobre o que cada parte da UI pode
 * fazer. Combinamos a entrada `mode` (intenção do chamador) com o estado
 * de aprovação real (vindo do backend via `metadata.approval_timestamp`)
 * em um único enum `effectiveMode`, propagado para baixo:
 *
 *   - `mode === "SOMENTE_LEITURA"`           → effectiveMode = "READ_ONLY"
 *       Nenhuma edição é permitida. Sem botão de "voltar a editar". A
 *       status bar exibe a tag "Somente leitura" + downloads.
 *   - `mode === "REVISAO"` + APROVADA        → effectiveMode = "APPROVED_LOCKED"
 *       Funciona como read-only (sem edições), MAS a status bar exibe o
 *       botão sublinhado "Voltar para edição" ao lado da tag aprovada,
 *       permitindo que o usuário desbloqueie a edição.
 *   - `mode === "REVISAO"` + NÃO APROVADA    → effectiveMode = "EDITING"
 *       Edição livre.
 *
 * A aprovação é detectada pelo `metadata.approval_timestamp` (não pelo
 * nome do aprovador, que é cosmético e pode não vir do backend).
 *
 * Filhos recebem flags derivadas, nunca o `effectiveMode` cru:
 *   - `editable = effectiveMode === "EDITING"` → SpeakersBar / TranscriptionList
 *   - O `TranscriptionStatusBar` recebe o `effectiveMode` inteiro porque
 *     precisa decidir entre 3 layouts distintos (in_review, approved,
 *     readonly).
 *
 * Props:
 *   - `mode`                  "REVISAO" | "SOMENTE_LEITURA"
 *   - `transcription`         {speakers, sentences} — versão "salva"
 *   - `mediaFile`             { token, filename } para o player (DOC_CREATION)
 *   - `mediaSrc`              alternativa: URL pública direta
 *   - `metadata`              { approved_by, approved_by_username,
 *                               approval_timestamp, last_saved_timestamp,
 *                               transcribed_at }. O `approved_by` (id) e o
 *                               `approved_by_username` (nome) vêm do backend
 *                               quando disponíveis; o painel não os usa
 *                               diretamente (apenas repassa para a status bar).
 *   - `loading` / `error`     estados externos para feedback visual
 *   - `saving` / `approving` / `cancelingApproval` flags repassadas à status bar
 *   - `onSave(content)`       handler de salvar
 *   - `onApprove(content)`    handler de aprovar
 *   - `onCancelApproval(content)` handler de "voltar para revisão"
 *   - `onDirtyChange(boolean)` notifica a página quando o estado dirty muda
 *                              (útil para guardar trocas de arquivo).
 *   - `forceLayout`           "auto" | "stack" | "split" (default: "auto")
 *   - `headerSlot`            nó React opcional renderizado acima do painel
 *                             (ex.: título do arquivo)
 *   - `jobId` / `transcriptionId` opcionais — não usados internamente, mas
 *                             permitem que callbacks externos correlacionem
 *                             a instância do painel com o job.
 */
function TranscriptionReviewPanel({
  mode = "REVISAO",
  transcription,
  mediaFile,
  mediaSrc,
  metadata,
  loading = false,
  error = null,
  saving = false,
  approving = false,
  cancelingApproval = false,
  onSave,
  onApprove,
  onCancelApproval,
  onDirtyChange,
  forceLayout = "auto",
  headerSlot,
  /* `jobId` e `transcriptionId` são repassados à status bar para que ela
     possa chamar os endpoints de download (PDF/DOCX/JSON) do backend. */
  jobId,
  transcriptionId,
}) {
  const { t } = useTranslation("common");
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  /* Refs e estados usados para implementar o "mini-player flutuante":
     - `sentinelRef`: div fininha posicionada exatamente onde o player
       começa. É ESTE elemento (que NUNCA muda de `position`) que o
       IntersectionObserver observa, em vez do próprio wrapper do player.
       Observar o wrapper diretamente cria um loop: ao virar `fixed`, o
       wrapper volta a aparecer (no canto inferior direito), o IO dispara
       intersecting=true, removemos `is-floating`, ele volta ao fluxo, e
       o ciclo se repete (flicker no scroll).
     - `playerWrapperRef`: o elemento que recebe `is-floating`. Não é
       observado pelo IO.
     - `playerHeight`: altura medida do wrapper enquanto NÃO está
       flutuante. Usada para renderizar um placeholder que preserva a
       altura no fluxo quando o player vira fixed (evita o conteúdo
       "subir" e o scroll do usuário escorregar). */
  const sentinelRef = useRef(null);
  const playerWrapperRef = useRef(null);
  const [playerFloating, setPlayerFloating] = useState(false);
  const [playerHeight, setPlayerHeight] = useState(0);
  /* O usuário pode dispensar manualmente o mini-player (botão "X"); nesse
     caso, só voltamos a mostrá-lo após o player original reaparecer no
     fluxo (rolando de volta para cima). */
  const [floatingDismissed, setFloatingDismissed] = useState(false);

  const [draft, setDraft] = useState(transcription || { speakers: [], sentences: [] });
  /* `activeSentenceIndex` é o ESTADO que reflete o player na lista. Trocamos
     o antigo `currentTime` (que mudava 10 vezes por segundo, causando
     re-renders em cascata na lista de parágrafos e congelamento da aba)
     por este índice, que muda apenas nas transições entre frases. O tempo
     bruto é mantido em ref (`currentTimeRef`) para quem precisar dele. */
  const [activeSentenceIndex, setActiveSentenceIndex] = useState(-1);
  const currentTimeRef = useRef(0);
  /* Duração total do arquivo de mídia (capturada via `onLoadedMetadata`).
     Exposta para a status bar mostrar "Duração hh:mm:ss". */
  const [mediaDuration, setMediaDuration] = useState(0);
  /* Tempo em segundos no qual devemos pausar automaticamente o player.
     Usado para implementar o requisito "ao clicar em uma frase, dar play
     a partir de start-1s e parar logo antes do início da próxima frase".
     Mantido em ref (não em state) porque o callback de tempo do player é
     `useCallback([])` e precisa ler o valor atual sem re-render. */
  const pendingPauseAtRef = useRef(null);

  /* Sincroniza o draft local quando o prop muda (ex.: após salvar/aprovar
     no servidor). Evita perder edições do usuário mantendo a referência
     do `transcription` apenas como "versão salva". */
  useEffect(() => {
    setDraft(transcription || { speakers: [], sentences: [] });
  }, [transcription]);

  /* Mantemos o `draft` mais recente em ref para que o callback do player
     (que precisa olhar as `sentences` atuais para calcular o índice ativo)
     seja estável (`useCallback([])`). Sem isso o callback recriaria a cada
     edição do usuário. */
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  /* `forceLayout` é mantido na assinatura por compatibilidade com chamadores
     existentes, mas como o layout agora é único, apenas observamos para
     devolver `undefined` previsível em testes. */
  void forceLayout;

  /* Modo efetivo da UI — derivado APENAS de `mode` + presença de
     `approval_timestamp` (o nome do aprovador é cosmético e pode estar
     ausente). É a única fonte de verdade para o que cada parte do
     painel pode ou não fazer; ver doc do componente para detalhes.
     Os filhos recebem flags derivadas (`editable`), não este enum. */
  const isApproved = Boolean(metadata?.approval_timestamp);
  const effectiveMode =
    mode === "REVISAO"
      ? isApproved
        ? "APPROVED_LOCKED"
        : "EDITING"
      : "READ_ONLY";
  const editable = effectiveMode === "EDITING";

  const dirty = useMemo(
    () => hasUnsavedChanges(transcription, draft),
    [transcription, draft],
  );

  /* Mantemos `onDirtyChange` em ref e disparamos o efeito SOMENTE quando
     `dirty` muda. Isto evita um loop infinito que existia antes: o pai
     passava `onDirtyChange` como uma lambda inline (nova ref a cada render)
     e seu callback `markDirty` chamava `forceTick` para forçar re-render —
     o que recriava a lambda — o que disparava este efeito de novo, etc. */
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);

  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);

  /* Seek + auto play: quando o usuário clica em um tempo / no texto de uma
     frase, posicionamos o player 1s antes do início da frase (para ouvir o
     contexto) e damos play se ainda não estava tocando. Se um `pauseAt`
     for fornecido (tipicamente o `end` da frase clicada), agendamos uma
     pausa automática nesse instante — assim o player toca exatamente a
     frase escolhida e para antes da próxima, facilitando a revisão de uma
     frase de cada vez. */
  const handleSeekAndPlay = useCallback((seconds, pauseAt) => {
    const player = playerRef.current;
    if (!player) return;
    const safeSeconds = Math.max(0, (Number(seconds) || 0) - 1);
    /* Programa a pausa automática ANTES do seek para que o tick do
       player já compare o tempo atualizado contra o pauseAt logo no
       primeiro frame após o seek. */
    pendingPauseAtRef.current = Number.isFinite(pauseAt) ? pauseAt : null;
    if (typeof player.seek === "function") {
      player.seek(safeSeconds);
    }
    if (typeof player.isPlaying === "function" && !player.isPlaying()) {
      try {
        const result = player.play?.();
        if (result && typeof result.catch === "function") {
          result.catch(() => {
            /* O navegador pode bloquear `play()` por falta de gesto do
               usuário. Como o seek é sempre disparado por clique, o caso
               mais comum (autoplay restrito) não se aplica aqui — mas
               ignoramos qualquer falha silenciosamente. */
          });
        }
      } catch (_) {
        /* idem: nunca deixar uma falha de play() interromper a seleção */
      }
    }
  }, []);

  /* Recebe o tempo do player (a cada ~100ms). Em vez de armazenar o tempo
     em state — o que dispararia re-render do painel inteiro 10×/seg —,
     calculamos aqui o índice da frase ativa e só atualizamos o state se
     ele mudou. Resultado: a lista de parágrafos só re-renderiza quando o
     player de fato cruza o limite entre duas frases.

     Também avalia o `pendingPauseAtRef`: se o tempo passou do pauseAt
     agendado por `handleSeekAndPlay`, pausa o player e zera o agendamento
     (uma única pausa por seek). */
  const handlePlayerTime = useCallback((seconds) => {
    currentTimeRef.current = seconds;
    const pauseAt = pendingPauseAtRef.current;
    if (
      pauseAt != null &&
      Number.isFinite(pauseAt) &&
      seconds >= pauseAt
    ) {
      pendingPauseAtRef.current = null;
      try {
        playerRef.current?.pause?.();
      } catch (_) {
        /* idem: nunca deixar uma falha de pause() escapar para o user */
      }
    }
    const sentences = draftRef.current?.sentences;
    const next = findActiveSentenceIndex(sentences, seconds);
    setActiveSentenceIndex((prev) => (prev === next ? prev : next));
  }, []);

  /* Sempre que o usuário pressiona play "manualmente" (botão do player),
     descartamos qualquer pausa programada — caso contrário ele bateria
     na pausa logo em seguida e ficaria com a sensação de que o play não
     funcionou. Como o painel não controla o player diretamente nesses
     casos, escutamos via prop `onPlay` (chamada nativa do <video>/<audio>). */
  const handlePlayerPlay = useCallback(() => {
    /* Mantemos o pause programado APENAS se o play veio de
       `handleSeekAndPlay` (no qual o ref já foi setado milissegundos
       antes). Se o usuário toca o botão de play do MediaPlayer
       diretamente — sem clicar em uma frase —, o ref ficou null no
       último ciclo, então não há nada para limpar. Mas se o usuário
       clicar em "Play" no meio de uma reprodução agendada, isso
       cancela o pause:  basicamente "tocar agora, e continuar". */
    /* Sem ação adicional necessária por enquanto — manter o hook
       disponível para evoluções futuras (por exemplo, marcar a frase
       atual como visitada). */
  }, []);

  const handlePlayerLoadedMetadata = useCallback((e) => {
    const d = e?.currentTarget?.duration;
    if (Number.isFinite(d)) setMediaDuration(d);
  }, []);

  /* Quando o conteúdo muda (ex.: um split que insere uma frase, ou troca
     de arquivo), reavaliamos o índice ativo com o tempo atual para evitar
     destacar uma frase incorreta até o próximo tick do player. */
  useEffect(() => {
    const sentences = draft?.sentences;
    const next = findActiveSentenceIndex(sentences, currentTimeRef.current);
    setActiveSentenceIndex((prev) => (prev === next ? prev : next));
  }, [draft]);

  const handleApprove = useCallback(() => {
    onApprove?.(draft);
  }, [draft, onApprove]);

  const handleSave = useCallback(() => {
    onSave?.(draft);
  }, [draft, onSave]);

  const handleCancelApproval = useCallback(() => {
    /* Para "cancelar aprovação" reusamos o /save: o backend zera os campos
       de aprovação. Enviamos o conteúdo atual para evitar perder edições. */
    onCancelApproval?.(draft);
  }, [draft, onCancelApproval]);

  /* Observa a visibilidade do SENTINEL (div fininha posicionada onde o
     player começa) no scroll da página. Quando o sentinel sai da viewport
     POR CIMA (scroll para baixo passou do player), entramos em modo
     flutuante; quando volta a aparecer, saímos. Observamos o sentinel
     em vez do próprio wrapper do player para evitar o loop descrito
     acima na declaração de `sentinelRef`. */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        /* Só consideramos "off-screen" quando o sentinel saiu por cima
           (`bottom < 0`). Se o sentinel não está intersectando porque
           ainda está abaixo da viewport (página acabou de carregar e
           o usuário não rolou), não queremos floating. */
        const isAbove = entry.boundingClientRect.bottom <= 0;
        if (isAbove && playerWrapperRef.current) {
          /* Captura a altura do wrapper IMEDIATAMENTE ANTES de aplicar
             `is-floating`. Isso garante que o `playerHeight` (usado
             pelo placeholder) esteja preenchido antes do React aplicar
             a transição visual — evitando um frame em que o conteúdo
             abaixo "sobe" e dispara o IO de novo (fonte do flicker). */
          const h = playerWrapperRef.current.offsetHeight;
          if (h > 0) setPlayerHeight((prev) => (prev === h ? prev : h));
        }
        setPlayerFloating(isAbove);
        if (!isAbove) setFloatingDismissed(false);
      },
      { threshold: 0, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [mediaFile, mediaSrc]);

  /* Mede a altura "natural" do wrapper do player enquanto ele está no
     fluxo. Esse valor é usado para renderizar um placeholder com a mesma
     altura quando o player vira flutuante — assim o conteúdo abaixo não
     "salta" para cima ao perder o player do fluxo, e o scroll do usuário
     permanece na mesma posição. Reagimos ao `ResizeObserver` para
     acompanhar mudanças (ex.: o usuário redimensiona o vídeo). */
  useEffect(() => {
    const el = playerWrapperRef.current;
    if (!el) return undefined;
    const update = () => {
      /* Importante: só cacheamos a altura quando NÃO estamos flutuante.
         A altura no modo flutuante é diferente (definida pelo CSS) e
         não deve substituir a altura "no fluxo". */
      if (!playerFloating && playerWrapperRef.current) {
        const h = playerWrapperRef.current.offsetHeight;
        if (h > 0) {
          setPlayerHeight((prev) => (prev === h ? prev : h));
        }
      }
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [playerFloating, mediaFile, mediaSrc]);

  const renderPlayer = () => {
    const showAsFloating = playerFloating && !floatingDismissed;
    /* "Recolhido": o player flutuante deveria aparecer (usuário rolou
       abaixo do player) mas o usuário fechou o mini-player. Em vez de
       sumir por completo, deixamos um ícone fixo no canto para reabrir
       sem precisar rolar até o topo. */
    const showReopenIcon = playerFloating && floatingDismissed;
    const wrapperClass = [
      "transcription-panel__player",
      showAsFloating ? "is-floating" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <>
        {/* Sentinel observado pelo IntersectionObserver. Como NUNCA muda
            de position, evita o loop em que o próprio wrapper, ao virar
            fixed, voltava a aparecer no canto inferior direito e
            disparava intersecting=true (resultando em flicker). */}
        <div
          ref={sentinelRef}
          className="transcription-panel__player-sentinel"
          aria-hidden="true"
        />
        {/* Placeholder que reserva no fluxo a altura do player original
            enquanto ele está flutuante, mantendo o scroll do usuário
            estável (sem "salto" do conteúdo abaixo). Vale também quando
            o player está recolhido (o player permanece "presente",
            apenas invisível, para que o áudio continue tocando). */}
        {(showAsFloating || showReopenIcon) && playerHeight > 0 ? (
          <div
            className="transcription-panel__player-placeholder"
            style={{ height: playerHeight }}
            aria-hidden="true"
          />
        ) : null}
        <Box
          ref={playerWrapperRef}
          className={wrapperClass}
          /* Quando recolhido, escondemos o wrapper visualmente mas o
             mantemos montado — o <audio>/<video> precisa seguir vivo
             para preservar tempo de reprodução e estado. */
          style={showReopenIcon ? { display: "none" } : undefined}
        >
          {showAsFloating ? (
            <Box className="transcription-panel__player-floating-bar">
              <Typography
                variant="caption"
                className="transcription-panel__player-floating-title"
              >
                {mediaFile?.filename || t("transcription.panel.miniPlayer")}
              </Typography>
              <Tooltip title={t("transcription.panel.miniPlayerClose")}>
                <IconButton
                  size="small"
                  onClick={() => setFloatingDismissed(true)}
                  aria-label={t("transcription.panel.miniPlayerClose")}
                  className="transcription-panel__player-floating-close"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
          <MediaPlayer
            ref={playerRef}
            file={mediaFile}
            src={mediaSrc}
            onTimeUpdate={handlePlayerTime}
            onLoadedMetadata={handlePlayerLoadedMetadata}
            onPlay={handlePlayerPlay}
          />
        </Box>
        {showReopenIcon ? (
          <Tooltip title={t("transcription.panel.miniPlayerReopen")}>
            <IconButton
              onClick={() => setFloatingDismissed(false)}
              aria-label={t("transcription.panel.miniPlayerReopen")}
              className="transcription-panel__player-reopen"
            >
              <PlayCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </>
    );
  };

  const renderStatus = () => (
    <TranscriptionStatusBar
      effectiveMode={effectiveMode}
      jobId={jobId}
      transcriptionId={transcriptionId}
      filename={mediaFile?.filename}
      transcription={draft}
      mediaDuration={mediaDuration}
      approverUsername={metadata?.approved_by_username}
      approvedById={metadata?.approved_by}
      approvalTimestamp={metadata?.approval_timestamp}
      lastSavedTimestamp={metadata?.last_saved_timestamp}
      transcribedAt={metadata?.transcribed_at}
      hasUnsavedChanges={dirty}
      saving={saving}
      approving={approving}
      cancelingApproval={cancelingApproval}
      onSave={handleSave}
      onApprove={handleApprove}
      /* Só passamos o handler de "voltar para edição" quando o modo
         efetivo é APPROVED_LOCKED. Em READ_ONLY (o modo SOMENTE_LEITURA)
         o usuário não pode reabrir; em EDITING já está livre. */
      onCancelApproval={
        effectiveMode === "APPROVED_LOCKED" ? handleCancelApproval : undefined
      }
    />
  );

  const renderSpeakers = () => (
    <SpeakersBar
      transcription={draft}
      editable={editable}
      onChange={setDraft}
    />
  );

  const renderList = () => (
    <TranscriptionList
      transcription={draft}
      editable={editable}
      activeSentenceIndex={activeSentenceIndex}
      onChange={setDraft}
      onSeek={handleSeekAndPlay}
    />
  );

  if (loading) {
    return (
      <Box className="transcription-panel transcription-panel--loading">
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: 12 }}>
          {t("transcription.panel.loading")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="transcription-panel transcription-panel--error">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} className="transcription-panel">
      {headerSlot ? (
        <Box className="transcription-panel__header-slot">{headerSlot}</Box>
      ) : null}

      <Box className="transcription-panel__top">
        {renderStatus()}
        {renderSpeakers()}
      </Box>

      <Box className="transcription-panel__main">
        {renderPlayer()}
        {renderList()}
      </Box>
    </Box>
  );
}

export default TranscriptionReviewPanel;
